import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `settlements` domain.
 *
 * Every claim was read out of svc-commission, not out of the spec. The four an
 * operator gets wrong, and which the copy therefore leads with:
 *
 *   - the settlement charges a MARGINAL slice (`cascadeProvider`: an ancestor
 *     keeps `PT_i − PT_{i+1}`, the leaf keeps its full `PT`), while the win/loss
 *     report splits on a GROSS basis fixed at the vantage (`company =
 *     base × PT_vantage`). The two are answers to different questions and
 *     cannot be reconciled against each other
 *   - "Period result" is `mlm_settlements.net`, surfaced twice under two names
 *     (`net` and `creditDelta`). Whether it is money depends on the cluster:
 *     `settlementAppliesCredit` returns `true` whenever `CREDIT_CUTOVER_PERIOD`
 *     is unset (prod sets no `CREDIT_*` var at all), so the run posts that exact
 *     figure through `postCreditMovement` and stamps `'applied'`. Only where the
 *     cutover owns the period is the row `'audited'` and `movementActual` the
 *     money — and with `CREDIT_PER_BET_MOVEMENT` off the movement consumer is
 *     never bound, so `movementActual` is a constant 0 rather than a gap
 *   - the date filter is a UTC calendar day (`[dayStart, dayStart+1d)`), and a
 *     Bangkok Monday week-start is stored at 17:00 the Sunday before
 *   - `agentScope` carries agent-LEAF node ids only, so a scoped master or
 *     senior never sees its own row — nor the company row
 */
export const settlementsGuide: DomainGuide = {
  domain: 'settlements',
  pages: [
    {
      id: 'settlements.list',
      route: '/settlements',
      permission: 'settlement:read',
      status: 'live',
      fields: [
        { key: 'period', kind: 'date' },
        { key: 'node', kind: 'text' },
        { key: 'currency', kind: 'text' },
        {
          key: 'periodKeep',
          kind: 'money-signed',
          formula: 'one line below, one provider:  keep = house × (ownPT − childPT)',
        },
        { key: 'net', kind: 'money-signed', formula: 'net = periodKeep + adjustments' },
        { key: 'status', kind: 'status' },
        { key: 'txId', kind: 'text' },
      ],
      cautionCount: 8,
      warningCount: 2,
    },
    {
      id: 'settlements.detail',
      route: '/settlements → click a row',
      permission: 'settlement:read',
      status: 'live',
      fields: [
        {
          key: 'periodResult',
          kind: 'money-signed',
          formula: 'periodResult = periodKeep + adjustments',
        },
        {
          key: 'movementActual',
          kind: 'money-signed',
          formula: 'movementActual = Σ movements in [weekStart, weekStart + 7d)',
        },
        { key: 'periodKeep', kind: 'money-signed' },
        { key: 'adjustments', kind: 'money-signed' },
        {
          key: 'providerKeep',
          kind: 'money-signed',
          formula: 'Σ providerKeep = periodKeep',
        },
        { key: 'providerPt', kind: 'percent' },
        { key: 'ptUsed', kind: 'percent' },
      ],
      cautionCount: 8,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
